import { Delete as DeleteIcon } from "@mui/icons-material";
import { Box } from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import * as logger from "common/logger";
import { makeRenderCellWithTooltip } from "components/common/dataGrid/cells";
import { makeChoicesColDef } from "components/common/dataGrid/choicesColumn";
import { makePrettyNumberColDef } from "components/common/dataGrid/prettyNumberColumn";
import * as labels from "config.labels";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingStore from "store/programPricing";

const NO_LIMIT_SUBLIMIT_VALUES = ["-", "x", "NA", "N/A"];

const makeColumns = ({
  catModel,
  catExposuresConfig,
  deleteExposure,
  columnEditabilityOverride,
  makePerilName,
  sortable,
  translateLabel,
}) => {
  return [
    {
      field: "_DESCRIPTION",
      headerName: translateLabel(labels.CAT_EXPOSURES_DESCRIPTION_COLUMN, {
        default: "Description/Details",
      }),
      type: "string",
      sortable,
      editable: true,
      width: 300,
      renderCell: makeRenderCellWithTooltip(),
    },
    makeChoicesColDef({
      field: "_REGION_PERIL",
      headerName: translateLabel(labels.CAT_EXPOSURES_REGION_PERIL_COLUMN, {
        default: "Region/Peril",
      }),
      width: 300,
      choices: !!catModel
        ? catExposuresConfig?.enums?.[`regionPeril_${catModel}`]?.choices
        : catExposuresConfig?.enums?.regionPeril?.choices,
      sortable,
    }),
    makePrettyNumberColDef({
      field: "_SUBLIMIT",
      headerName: translateLabel(labels.CAT_EXPOSURES_SUBLIMIT_COLUMN, {
        default: "Sublimit",
      }),
      width: 200,
      sortable,
      editable: true,
      extraValues: NO_LIMIT_SUBLIMIT_VALUES,
      cellClassName: (params) =>
        params?.row?._OVERRIDE_SUBLIMIT != null ? "overridden" : "",
    }),
    makePrettyNumberColDef({
      field: "_VALUE",
      headerName: translateLabel(labels.CAT_EXPOSURES_VALUE_COLUMN, {
        default: "Value",
      }),
      width: 200,
      sortable,
      editable: true,
      cellClassName: (params) =>
        params?.row?._OVERRIDE_VALUE != null ? "overridden" : "",
    }),
    makePrettyNumberColDef({
      field: "_EXPIRING_VALUE",
      headerName: translateLabel(labels.CAT_EXPOSURES_EXPIRING_VALUE_COLUMN, {
        default: "Expiring Value",
      }),
      width: 200,
      sortable,
      editable: false,
      cellClassName: "not-editable",
    }),
    {
      field: "_SUBLIMIT_ZONE",
      headerName: translateLabel(labels.CAT_EXPOSURES_SUBLIMIT_ZONE_COLUMN, {
        default: "Sublimit Zone",
      }),
      type: "string",
      sortable,
      editable: false,
      width: 200,
      valueFormatter: makePerilName,
    },
    makeChoicesColDef({
      field: "_CAT_HAZARD",
      headerName: translateLabel(labels.CAT_EXPOSURES_CAT_HAZARD_COLUMN, {
        default: "Cat Hazard",
      }),
      choices: catExposuresConfig?.enums?.catHazard?.choices,
      sortable,
      cellClassName: (params) =>
        params?.row?._OVERRIDE_CAT_HAZARD != null ? "overridden" : "",
    }),
    makeChoicesColDef({
      field: "_RISK_MANAGEMENT",
      headerName: translateLabel(labels.CAT_EXPOSURES_RISK_MANAGEMENT_COLUMN, {
        default: "Risk Management",
      }),
      choices: catExposuresConfig?.enums?.riskManagement?.choices,
      sortable,
    }),
    makePrettyNumberColDef({
      field: "_EXPOSURE_RATE",
      headerName: translateLabel(labels.CAT_EXPOSURES_EXPOSURE_RATE_COLUMN, {
        default: "GU Exp Rate (%)",
      }),
      width: 100,
      dp: 3,
      multiplier: 100,
      sortable,
      editable: true,
      cellClassName: (params) =>
        params?.row?._OVERRIDE_EXPOSURE_RATE != null ? "overridden" : "",
    }),
    makePrettyNumberColDef({
      field: "_ADJUSTED_EXPOSURE_RATE",
      headerName: translateLabel(
        labels.CAT_EXPOSURES_ADJUSTED_EXPOSURE_RATE_COLUMN,
        {
          default: "Adj Exp Rate (%)",
        }
      ),
      width: 100,
      dp: 3,
      multiplier: 100,
      sortable,
      editable: false,
      cellClassName: "not-editable",
    }),
    makePrettyNumberColDef({
      field: "_TOTAL_VALUE",
      headerName: translateLabel(labels.CAT_EXPOSURES_TOTAL_VALUE_COLUMN, {
        default: "Total Value",
      }),
      width: 200,
      align: "right",
      sortable,
      editable: false,
      cellClassName: "not-editable",
    }),
    makePrettyNumberColDef({
      field: "_FGU_PREMIUM",
      headerName: translateLabel(labels.CAT_EXPOSURES_FGU_PREMIUM_COLUMN, {
        default: "FGU Prem",
      }),
      width: 200,
      align: "right",
      sortable,
      editable: false,
      cellClassName: "not-editable",
    }),
    {
      field: "actions",
      type: "actions",
      width: 80,
      hide: !deleteExposure,
      getActions: (params) =>
        params?.id != null && !params?.row?.__IS_BLANK__
          ? [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label={"Delete"}
                onClick={() => {
                  deleteExposure(params?.id);
                }}
              />,
            ]
          : [],
    },
  ]
    .filter((column) => !column.hide)
    .map((column) => ({
      ...column,
      editable: columnEditabilityOverride?.[column.field] ?? column.editable,
    }));
};

const CatExposuresGrid = ({
  catModel,
  exposures,
  updateExposure,
  deleteExposure,
  columnEditabilityOverride,
  sortable = false,
  initialSortModel = null,
  disabled = false,
  hideExpiring = false,
}) => {
  const { data: catExposuresConfig } = staticDataService.useConfigQuery(
    "cat_exposures"
  );

  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const programPricing = useSelector(programPricingStore.select);
  const makePerilName = useCallback(
    (peril) => programPricing?.coverageConfig?.perils?.[peril]?.name ?? peril,
    [programPricing?.coverageConfig?.perils]
  );

  const [columnWidths, setColumnWidths] = useState({});

  const handleColumnWidthChange = useCallback((params) => {
    setColumnWidths((prev) => ({
      ...prev,
      [params.colDef.field]: params.width,
    }));
  }, []);

  const columns = useMemo(
    () =>
      makeColumns({
        catModel,
        catExposuresConfig,
        deleteExposure,
        columnEditabilityOverride,
        makePerilName,
        sortable,
        translateLabel,
      })
        .map((column) => {
          const userWidth = columnWidths[column.field];
          return userWidth ? { ...column, width: userWidth } : column;
        })
        .map((column) => (disabled ? { ...column, editable: false } : column)),
    [
      catModel,
      catExposuresConfig,
      deleteExposure,
      columnEditabilityOverride,
      makePerilName,
      sortable,
      translateLabel,
      columnWidths,
      disabled,
    ]
  );

  return (
    <Box
      sx={{
        width: "100%",
        "& .not-editable": {
          bgcolor: "#EFEFEF",
        },
        "& .overridden": {
          color: "white",
          backgroundColor: "primary.main",
          fontWeight: "bold",
        },
      }}
    >
      <DataGrid
        rows={exposures}
        columns={columns}
        disableSelectionOnClick
        hideFooter
        autoHeight
        onColumnWidthChange={handleColumnWidthChange}
        columnVisibilityModel={{
          _SUBLIMIT_ZONE: false,
          _EXPIRING_VALUE: !hideExpiring,
        }}
        onCellEditStart={(params, event) => {
          if (params?.reason === "deleteKeyDown") {
            updateExposure(params?.id)({ field: params?.field, value: null });
            event.defaultMuiPrevented = true;
            return;
          }
        }}
        processRowUpdate={(newRow, oldRow) => {
          Object.entries(newRow)
            .filter(([field, value]) => oldRow?.[field] !== value)
            .map(([field, value]) =>
              field === "_SUBLIMIT" &&
              NO_LIMIT_SUBLIMIT_VALUES.includes(value.toString().trim())
                ? [field, "__NO_SUBLIMIT__"]
                : [field, value]
            )
            .forEach(([field, value]) => {
              updateExposure(newRow.id)({ field, value });
            });
          return newRow;
        }}
        onProcessRowUpdateError={(error) => logger.error(error)}
        initialState={{
          sorting: {
            sortModel: initialSortModel,
          },
        }}
      />
    </Box>
  );
};

export default CatExposuresGrid;
